/**
 * DICOM Modality (0008,0060) code → full English name.
 *
 * Source: DICOM PS3.3 C.7.3.1.1.1 "Modality". We ship the common
 * clinically-used codes — exotic ones (e.g. DOC, FID, IOL, PLAN)
 * fall back to the raw code so the UI never shows "undefined".
 *
 * Used for tooltips on modality chips, filter buttons, and banners.
 */
const NAMES: Record<string, string> = {
  AR:  "Autorefraction",
  AS:  "Angioscopy",
  ASMT:"Content Assessment Results",
  AU:  "Audio",
  BDUS:"Bone Densitometry (Ultrasound)",
  BI:  "Biomagnetic Imaging",
  BMD: "Bone Densitometry (X-Ray)",
  CR:  "Computed Radiography",
  CT:  "Computed Tomography",
  CTPT:"CT / Positron Emission Tomography (hybrid)",
  DG:  "Diaphanography",
  DOC: "Document",
  DX:  "Digital Radiography",
  ECG: "Electrocardiography",
  EEG: "Electroencephalography",
  EMG: "Electromyography",
  EOG: "Electrooculography",
  EPS: "Cardiac Electrophysiology",
  ES:  "Endoscopy",
  FID: "Fiducials",
  GM:  "General Microscopy",
  HC:  "Hard Copy",
  HD:  "Hemodynamic Waveform",
  IO:  "Intra-Oral Radiography",
  IOL: "Intraocular Lens Data",
  IVOCT:"Intravascular Optical Coherence Tomography",
  IVUS:"Intravascular Ultrasound",
  KER: "Keratometry",
  KO:  "Key Object Selection",
  LEN: "Lensometry",
  LS:  "Laser Surface Scan",
  MG:  "Mammography",
  MR:  "Magnetic Resonance",
  NM:  "Nuclear Medicine",
  OAM: "Ophthalmic Axial Measurements",
  OCT: "Optical Coherence Tomography",
  OP:  "Ophthalmic Photography",
  OPM: "Ophthalmic Mapping",
  OPT: "Ophthalmic Tomography",
  OPV: "Ophthalmic Visual Field",
  OSS: "Optical Surface Scan",
  OT:  "Other",
  PLAN:"Plan",
  PR:  "Presentation State",
  PT:  "Positron Emission Tomography (PET)",
  PX:  "Panoramic X-Ray",
  REG: "Registration",
  RESP:"Respiratory Waveform",
  RF:  "Radio Fluoroscopy",
  RG:  "Radiographic Imaging (conventional film/screen)",
  RT:      "Radiotherapy (family code — covers RTSTRUCT, RTPLAN, RTDOSE, RTIMAGE, RTRECORD)",
  RTDOSE:  "Radiotherapy Dose",
  RTIMAGE: "Radiotherapy Image",
  RTPLAN:  "Radiotherapy Plan",
  RTRECORD:"Radiotherapy Treatment Record",
  RTSTRUCT:"Radiotherapy Structure Set",
  RWV: "Real World Value Map",
  SEG: "Segmentation",
  SM:  "Slide Microscopy (Digital Pathology)",
  SMR: "Stereometric Relationship",
  SR:  "Structured Report",
  SRF: "Subjective Refraction",
  ST:  "Single-Photon Emission CT (SPECT)",
  TG:  "Thermography",
  US:  "Ultrasound",
  VA:  "Visual Acuity",
  XA:  "X-Ray Angiography",
  XC:  "External-Camera Photography",
};

export function modalityName(code: string | null | undefined): string {
  if (!code) return "";
  return NAMES[code.toUpperCase()] ?? code;
}

/** "CT — Computed Tomography" for tooltips. For codes that aren't in
 *  the table we still produce something descriptive rather than just
 *  echoing the code — hovering `FOO` and reading `FOO` is no better
 *  than no tooltip at all. */
export function modalityTitle(code: string | null | undefined): string {
  if (!code) return "";
  const full = NAMES[code.toUpperCase()];
  return full ? `${code} — ${full}` : `${code} — (unrecognised DICOM modality code)`;
}
